import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Inbox,
  Loader2,
  Mail,
  RefreshCw,
  Search,
  ShieldOff,
  XCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { api, formatApiError } from "@/lib/api";

const LIMIT_OPTIONS = [
  { value: "25", label: "25 rows" },
  { value: "50", label: "50 rows" },
  { value: "100", label: "100 rows" },
  { value: "250", label: "250 rows" },
];

const SINCE_OPTIONS = [
  { value: "all", label: "Any time" },
  { value: "1h", label: "Last hour" },
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
];

const STATUS_META = {
  delivered: { icon: CheckCircle2, classes: "bg-emerald-100 text-emerald-700", label: "Delivered" },
  pending: { icon: Clock, classes: "bg-slate-100 text-slate-700", label: "Pending" },
  deferred: { icon: Clock, classes: "bg-amber-100 text-amber-700", label: "Deferred" },
  bounce: { icon: AlertTriangle, classes: "bg-rose-100 text-rose-700", label: "Bounced" },
  dropped: { icon: XCircle, classes: "bg-rose-100 text-rose-700", label: "Dropped" },
  suppressed: { icon: ShieldOff, classes: "bg-rose-100 text-rose-700", label: "Suppressed" },
  suppressed_by_setting: { icon: ShieldOff, classes: "bg-rose-100 text-rose-700", label: "Workspace-disabled" },
  failed: { icon: XCircle, classes: "bg-rose-100 text-rose-700", label: "Send failed" },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { icon: Clock, classes: "bg-slate-100 text-slate-700", label: status || "—" };
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${meta.classes}`}
      data-testid={`delivery-status-pill-${status}`}
    >
      <Icon size={12} strokeWidth={2.4} />
      {meta.label}
    </span>
  );
}

function fmtDt(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString();
  } catch {
    return value;
  }
}

function sinceFromOption(opt) {
  if (!opt || opt === "all") return null;
  const now = Date.now();
  if (opt === "1h") return new Date(now - 60 * 60 * 1000).toISOString();
  if (opt === "24h") return new Date(now - 24 * 60 * 60 * 1000).toISOString();
  if (opt === "7d") return new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString();
  if (opt === "30d") return new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString();
  return null;
}

export default function AdminEmailDeliveryPage() {
  const [recipient, setRecipient] = useState("");
  const [recipientApplied, setRecipientApplied] = useState("");
  const [sinceOpt, setSinceOpt] = useState("7d");
  const [limit, setLimit] = useState("50");
  const [sortKey, setSortKey] = useState("sent_at");
  const [sortDir, setSortDir] = useState("desc");

  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: [
      "admin-email-delivery",
      { recipient: recipientApplied, since: sinceOpt, limit },
    ],
    queryFn: async () => {
      const params = { limit: Number(limit) };
      if (recipientApplied.trim()) params.recipient = recipientApplied.trim();
      const sinceIso = sinceFromOption(sinceOpt);
      if (sinceIso) params.since = sinceIso;
      const res = await api.get("/admin/notifications/delivery-status", { params });
      return Array.isArray(res.data) ? res.data : [];
    },
    keepPreviousData: true,
  });

  const rows = useMemo(() => {
    const list = Array.isArray(data) ? [...data] : [];
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      const av = a?.[sortKey];
      const bv = b?.[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return -1 * dir;
      if (bv == null) return 1 * dir;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return list;
  }, [data, sortKey, sortDir]);

  const summary = useMemo(() => {
    const total = rows.length;
    const counts = {};
    for (const r of rows) {
      const s = r?.final_status || "pending";
      counts[s] = (counts[s] || 0) + 1;
    }
    return { total, counts };
  }, [rows]);

  const onSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const applyRecipient = () => setRecipientApplied(recipient);

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-4 py-8" data-testid="admin-email-delivery-page">
      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Mail size={16} />
          Admin · Notifications
        </div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          Email delivery status
        </h1>
        <p className="max-w-3xl text-sm text-slate-600">
          Joins internal <code>video_ready_notification</code> audit events with SendGrid Event-Webhook
          callbacks so you can confirm an autorun notification actually reached the recipient —
          without needing Gmail access.
        </p>
      </header>

      <section
        className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
        data-testid="admin-email-delivery-filters"
      >
        <div className="grid gap-3 md:grid-cols-[1fr,180px,180px,auto]">
          <div className="relative">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyRecipient()}
              placeholder="Filter by recipient e.g. mike@eyvo.com (leave blank for all)"
              className="pl-9"
              data-testid="admin-email-delivery-recipient-input"
            />
          </div>
          <Select value={sinceOpt} onValueChange={setSinceOpt}>
            <SelectTrigger data-testid="admin-email-delivery-since-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SINCE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={limit} onValueChange={setLimit}>
            <SelectTrigger data-testid="admin-email-delivery-limit-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LIMIT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="default"
              onClick={applyRecipient}
              data-testid="admin-email-delivery-apply-btn"
            >
              Apply
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => refetch()}
              data-testid="admin-email-delivery-refresh-btn"
              title="Refresh"
            >
              {isFetching ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            </Button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500" data-testid="admin-email-delivery-summary">
          <span className="rounded-full bg-slate-100 px-2 py-0.5">Total: <strong>{summary.total}</strong></span>
          {Object.entries(summary.counts).map(([k, v]) => (
            <span key={k} className="rounded-full bg-slate-50 px-2 py-0.5">
              {STATUS_META[k]?.label || k}: <strong>{v}</strong>
            </span>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        {error ? (
          <div className="p-6 text-sm text-rose-700" data-testid="admin-email-delivery-error">
            Failed to load: {formatApiError(error)}
          </div>
        ) : isLoading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-slate-500" data-testid="admin-email-delivery-loading">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-12 text-sm text-slate-500" data-testid="admin-email-delivery-empty">
            <Inbox size={28} className="text-slate-400" />
            <p>No notification deliveries match the current filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-email-delivery-table">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <ThSort label="Sent at" k="sent_at" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <ThSort label="Recipient" k="recipient" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <ThSort label="Workflow" k="workflow_name" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <ThSort label="Run" k="run_number_label" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <ThSort label="Final status" k="final_status" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <th className="px-4 py-2 font-medium">Webhook trail</th>
                  <th className="px-4 py-2 font-medium">SendGrid msg id</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr
                    key={`${r.run_id || idx}-${r.sendgrid_message_id || idx}`}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60"
                    data-testid={`admin-email-delivery-row-${idx}`}
                  >
                    <td className="px-4 py-3 align-top text-slate-700">{fmtDt(r.sent_at)}</td>
                    <td className="px-4 py-3 align-top text-slate-700">{r.recipient || "—"}</td>
                    <td className="px-4 py-3 align-top text-slate-700">{r.workflow_name || "—"}</td>
                    <td className="px-4 py-3 align-top font-mono text-xs text-slate-600">
                      {r.run_number_label || (r.run_id ? r.run_id.slice(0, 8) : "—")}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex flex-col gap-1">
                        <StatusPill status={r.final_status} />
                        {(r.alerts_dispatched || []).map((a, ai) => (
                          <span
                            key={`alert-${idx}-${ai}`}
                            className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800"
                            data-testid={`alert-dispatched-pill-${idx}-${ai}`}
                            title={`${a.event_type} → alert sent to ${a.alert_recipient} (${a.alert_message_id || 'no msg id'})`}
                          >
                            <AlertTriangle size={11} strokeWidth={2.4} />
                            Alert {a.status === 'sent' ? 'dispatched' : a.status || 'failed'} → {a.alert_recipient || '?'}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <WebhookTrail events={r.webhook_events || []} />
                    </td>
                    <td className="px-4 py-3 align-top">
                      <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">
                        {r.sendgrid_message_id || "—"}
                      </code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function ThSort({ label, k, sortKey, sortDir, onSort }) {
  const active = sortKey === k;
  return (
    <th
      className="cursor-pointer select-none px-4 py-2 font-medium hover:text-slate-700"
      onClick={() => onSort(k)}
      data-testid={`admin-email-delivery-th-${k}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (
          <span className="text-slate-400">{sortDir === "asc" ? "↑" : "↓"}</span>
        )}
      </span>
    </th>
  );
}

function WebhookTrail({ events }) {
  if (!events || events.length === 0) {
    return <span className="text-xs text-slate-400">no webhook events yet</span>;
  }
  return (
    <ol className="space-y-1">
      {events.map((e, i) => (
        <li key={`${e.sg_event_id || i}`} className="flex items-baseline gap-2 text-xs text-slate-600">
          <span className="font-mono text-[11px] text-slate-400">{fmtDt(e.at)}</span>
          <span className="font-medium">{e.event}</span>
          {e.reason && <span className="text-slate-500">— {e.reason}</span>}
        </li>
      ))}
    </ol>
  );
}
